import { ACCESS_TOKEN_KEY, ENV_HEADER_KEY, EZ_GOOGLE_RECAPTCHA_ID, EZ_RECAPTCHA_SCRIPT_SRC } from "../constants/wcConstants";
import { HTTP_METHODS } from "../eums/httpRequestsMethods";
import { getEnvName, getGlobalVal, getRequestUrlWithParams, resolvePromiseWithError, resolvePromiseWithSuccess } from "../utilities/utils";

// Global callback for handling invalid access token
let invalidAccessTokenCallback: (() => void) | null = null;

const setInvalidAccessTokenCallback = (callback: () => void) => {
    invalidAccessTokenCallback = callback;
}

export const waitForSec = (ms: number) => {
    return new Promise<void>((resolve) => {
        setTimeout(() => {
            resolve();
        }, ms);
    });
}

export const waitForRecaptcha = () => {
    return new Promise((resolve, reject) => {
        if (window.grecaptcha) {
            return resolve(window.grecaptcha);
        }

        const checkInterval = setInterval(() => {
            if (window.grecaptcha) {
                clearInterval(checkInterval);
                resolve(window.grecaptcha);
            }
        }, 200);

        // Optional timeout to avoid waiting indefinitely
        setTimeout(() => {
            clearInterval(checkInterval);
            reject(new Error("reCAPTCHA failed to load in time"));
        }, 10000);
    });
}

const loadRecaptcha = async () => {
    return new Promise<void>((resolve, reject) => {
        if (document.getElementById(EZ_GOOGLE_RECAPTCHA_ID) != null) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.async = true;
        script.setAttribute('id', EZ_GOOGLE_RECAPTCHA_ID);
        script.setAttribute('src', EZ_RECAPTCHA_SCRIPT_SRC);
        script.setAttribute('type', 'text/javascript');
        window.document.head.appendChild(script);
        script.onload = () => resolve();
    });
}

const getRecaptchaToken = async () => {
    await waitForRecaptcha();

    return new Promise(async (resolve, reject) => {
        window?.grecaptcha?.ready(function () {
            window?.grecaptcha?.execute(getGlobalVal('RECAPTCHA_SITE_KEY'), { action: 'EZQUOTE_WC_API_REQUEST' }).then(function (token: any) {
                resolve(token);
            });
        });
    });
}

const saveUserAccessToken = (response: any) => {
    if (response?.headers?.get?.(ACCESS_TOKEN_KEY)) {
        sessionStorage.setItem(ACCESS_TOKEN_KEY, response?.headers?.get?.(ACCESS_TOKEN_KEY));
    }
}

const getUserAccessToken = () => {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
}

export const executeDataRequest = async (path: string, inputs: any, headers: any = {}, verifyReCaptcha = true) => {
    try {
        const dataRequestUrl = getRequestUrlWithParams(path, inputs?.params)
    
        if (!dataRequestUrl) {
            return await resolvePromiseWithError(
                'Invalid API URL provided.', dataRequestUrl
            );
        }
    
        const fetchConfig: any = {
            method: inputs?.method || HTTP_METHODS.GET,
            credentials: 'include',
            headers: {
                "Content-Type": "application/json" 
            }
        };
    
        if (headers && headers instanceof Object) {
            Object.assign(fetchConfig.headers, headers);
        }
        
        // set access token in the request header 
        fetchConfig.headers[ACCESS_TOKEN_KEY] = getUserAccessToken();

        // identify which environment the widget is running against
        const envName = getEnvName();
        if (envName) {
            fetchConfig.headers[ENV_HEADER_KEY] = envName;
        }

        if(fetchConfig.method != HTTP_METHODS.GET && inputs.postData) {
            fetchConfig.body = inputs.postData;
        }
       
        if (verifyReCaptcha) {
            await loadRecaptcha();
            const rCaptchaToken = await getRecaptchaToken();

            if (!rCaptchaToken) {
                return await resolvePromiseWithError(
                    'Recaptcha token not found', dataRequestUrl, fetchConfig
                );
            }

            fetchConfig.headers['grctoken'] = rCaptchaToken;
        }
    
        let apiResponse: any = await fetch(dataRequestUrl, fetchConfig);
        
        // get sesion token from response headers and save to localstorage
        saveUserAccessToken(apiResponse);

        apiResponse = await apiResponse.json();
        
        // Check for INVALID_ACCESS_TOKEN in the response
        if (apiResponse?.success === false && 
            apiResponse?.messages && 
            Array.isArray(apiResponse.messages) && 
            apiResponse.messages.includes('INVALID_ACCESS_TOKEN')) {
            
            // Clear the access token from sessionStorage
            sessionStorage.removeItem(ACCESS_TOKEN_KEY);
            
            // Call the callback to handle session restart
            if (invalidAccessTokenCallback) {
                invalidAccessTokenCallback();
            }
            
            return await resolvePromiseWithError('INVALID_ACCESS_TOKEN');
        }
        
        if (apiResponse?.error) {
            return await resolvePromiseWithError(apiResponse.error);
        }
    
        return await resolvePromiseWithSuccess(apiResponse);
    } catch (error) {
        return await resolvePromiseWithError(error);
    }
}

const execute = async (path: string, inputs: any = null, headers: any = {}, verifyReCaptcha = true) => {
    if (!inputs) {
        inputs = {};
    }

    return await executeDataRequest(path, inputs, headers, verifyReCaptcha);
}

const DataRequest = {
    execute,
    setInvalidAccessTokenCallback
};

export default DataRequest;